import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useRouter from 'common/hooks/use-router';
import SectionTitle from 'common/components/SectionTitle/SectionTitle';
import PageLoading from 'common/components/PageLoading/PageLoading';
import Icon from 'components/Icon/Icon';
import AlertsContext from 'common/providers/alerts';
import Calendar from 'common/components/Calendar/Calendar';
import { Event, ModifiedEvent } from 'types/Event';
import useAuth from 'common/hooks/useAuth';
import useItem from 'common/hooks/useItem';
import EventDrawer from 'pages/Agenda/components/EventDrawer';
import { BiCalendarCheck } from 'react-icons/bi';
import StickyWrapper from 'common/components/StickyWrapper/StickyWrapper';
import { Link } from 'react-router-dom';
import Button from 'common/components/Button/Button';
import useAsync from 'common/hooks/useAsync';
import fetchJSON from 'common/utils/fetchJSON';
import {
  calculateDateRangeForView,
  getDateFromRange,
  areDateRangesEqual,
} from 'common/components/Calendar/calendarUtils';
import {
  detectDSTPeriodMismatch,
  applyDSTCorrection,
  getTimezoneInfo,
} from 'common/utils/dateUtils';
import dayjs from 'dayjs';

function Availability() {
  const router = useRouter();
  const { t } = useTranslation();
  const { setAlert } = useContext(AlertsContext);
  const { user: mediatorUser, errorMessage } = useAuth();
  const [event, setEvent] = useState<Event | null>(null); // Event being actually modified
  const [selectedEvent, setSelectedEvent] = useState<ModifiedEvent | null>(null); // Event displayed during the modification

  const operator = useMemo(() => mediatorUser?.operator, [mediatorUser]);

  useEffect(() => {
    if (errorMessage) {
      setAlert(errorMessage, 'danger');
    }
  }, [errorMessage, setAlert]);

  // Utilisation de useAsync pour les disponibilités (availability)
  const fetchAvailabilitiesAsync = useCallback(
    async ({ start, end, operatorId }: { start: Date; end: Date; operatorId?: string }) => {
      try {
        const response = await fetchJSON({
          url: 'events/availability',
          method: 'POST',
          payload: { start, end, operatorId },
        });

        return response.data;
      } catch (error) {
        console.error('Erreur lors de la récupération des disponibilités:', error);
        // Retourner un tableau vide en cas d'erreur
        return [];
      }
    },
    []
  );

  const {
    data: availabilities,
    isProcessing: isAvailabilitiesFetching,
    execute: fetchAvailabilities,
  } = useAsync<Event[]>(fetchAvailabilitiesAsync, {
    cachePrefix: 'availabilities_planning',
    ready: Boolean(operator),
    storeData: true,
    manual: true,
  });

  const { saveItem, updateItem, removeItem } = useItem('events', null, {});

  // Référence pour stocker la dernière plage chargée
  const lastLoadedRangeRef = useRef<{ start: Date; end: Date; view: string } | null>(null);

  // Fonction pour charger les disponibilités pour une plage de dates
  const loadAvailabilitiesForDateRange = useCallback(
    (date: Date, view: 'month' | 'week' | 'day' | 'agenda', range?: { start: Date; end: Date }) => {
      if (!operator) return;

      // Calculer la plage optimisée en fonction de la vue
      const { start, end } = range || calculateDateRangeForView(date, view);

      // Vérifier si la plage a déjà été chargée
      if (
        lastLoadedRangeRef.current &&
        areDateRangesEqual(lastLoadedRangeRef.current, { start, end, view })
      ) {
        return;
      }

      // Mettre à jour la référence
      lastLoadedRangeRef.current = { start, end, view };

      // Charger les disponibilités
      fetchAvailabilities({
        start,
        end,
        operatorId: operator?.id || operator,
      });
    },
    [operator, fetchAvailabilities]
  );

  const [calendarView, setCalendarView] = useState<'month' | 'week' | 'day' | 'agenda'>('week');

  // Track the current view from Calendar component
  const [currentCalendarView, setCurrentCalendarView] = useState<
    'month' | 'week' | 'day' | 'agenda'
  >('week');

  // Fonction pour détecter la vue actuelle en fonction de la plage de dates
  const detectViewFromRange = useCallback(
    (range: Date[] | { start: Date; end: Date }): 'month' | 'week' | 'day' | 'agenda' => {
      // Si c'est un tableau de dates, c'est probablement une vue mois
      if (Array.isArray(range) && range.length > 7) {
        return 'month';
      }

      // Si c'est un objet avec start et end
      if (!Array.isArray(range)) {
        const daysDiff = Math.round(
          Math.abs((range.end.getTime() - range.start.getTime()) / (1000 * 60 * 60 * 24))
        );

        // More precise detection based on typical calendar ranges
        if (daysDiff <= 2) return 'day';
        if (daysDiff <= 14) return 'week'; // Allow some buffer for week calculations
        if (daysDiff <= 60) return 'month'; // Allow buffer for month calculations
        return 'agenda';
      }

      // Par défaut, on utilise la vue actuelle du calendrier
      return currentCalendarView;
    },
    [currentCalendarView]
  );

  // Fonction pour gérer le changement de date ou de vue du calendrier
  const handleRangeChange = useCallback(
    (range: Date[] | { start: Date; end: Date }) => {
      if (!operator) return;

      // Détecter la vue actuelle en fonction de la plage
      const detectedView = detectViewFromRange(range);

      // Mettre à jour la vue si elle a changé
      if (detectedView !== calendarView) {
        setCalendarView(detectedView);
      }

      // Extraire la date de référence de la plage
      const date = getDateFromRange(range);

      // Charger les disponibilités pour la nouvelle plage de dates
      // Always use the detected view for consistency
      loadAvailabilitiesForDateRange(
        date,
        detectedView,
        'start' in range ? { start: range.start, end: range.end } : undefined
      );
    },
    [operator, loadAvailabilitiesForDateRange, calendarView, detectViewFromRange]
  );

  // Handle view changes from the Calendar component
  const handleViewChange = useCallback((view: 'month' | 'week' | 'day' | 'agenda') => {
    setCurrentCalendarView(view);
    setCalendarView(view);
  }, []);

  const handleEventChanged = useCallback(async () => {
    // Récupérer les valeurs de la plage actuelle
    const { start, end } = lastLoadedRangeRef.current || {};

    // Charger les disponibilités
    const newAvailabilities: any = await fetchAvailabilities({
      start,
      end,
      operatorId: operator?.id || operator,
    });
    setEvent((prevEvent) => newAvailabilities?.find((e: Event) => e.id === prevEvent?.id) || null);
    setSelectedEvent(
      (prevEvent) => newAvailabilities?.find((e: Event) => e.id === prevEvent?.id) || null
    );
  }, [fetchAvailabilities, operator]);

  const handleDeleteEvent = useCallback(
    async (recurrenceEditMode: 'all' | 'future' | 'single' | null) => {
      try {
        if (event && event.id) {
          if (event.eventType === 'availability' && recurrenceEditMode === 'future') {
            // update the master event to repeat until beginning of current event
            await updateItem(event.id, {
              recurrenceEditMode: 'all', // /!\ On veut modifier l'évènement maître en entier
              repeatUntil: dayjs(event.start).startOf('day').toDate(),
            });
          } else if (event.eventType === 'availability' && recurrenceEditMode === 'all') {
            // delete the master event
            await removeItem(event.parentRepeatedEvent);
          } else {
            // delete the single event
            await removeItem(event.id);
          }

          // Recharger les disponibilités avec les valeur de la plage actuelle
          const { start, end } = lastLoadedRangeRef.current || {};
          await fetchAvailabilities({
            start,
            end,
            operatorId: operator?.id || operator,
          });
          setEvent(null);
          setSelectedEvent(null);
        }
      } catch (error) {
        console.error('Error deleting event:', error);
      }
    },
    [event, fetchAvailabilities, operator, updateItem, removeItem]
  );

  // Effet initial pour charger les disponibilités au démarrage
  useEffect(() => {
    if (operator) {
      // Charger les disponibilités pour la vue par défaut (semaine)
      loadAvailabilitiesForDateRange(new Date(), 'week');
    }
  }, [operator, loadAvailabilitiesForDateRange]);

  const handleSubmitEvent = useCallback(
    async (eventData: Event) => {
      try {
        // First save or update the event
        const result = eventData.id
          ? await updateItem(eventData.id, eventData)
          : await saveItem(eventData);

        // Recharger les disponibilités avec les valeur de la plage actuelle
        const { start, end, view } = lastLoadedRangeRef.current || {};
        if (dayjs(eventData.start).isBetween(start, end)) {
          await fetchAvailabilities({
            start,
            end,
            operatorId: operator?.id || operator,
          });
        } else {
          // Then refresh the events list for the range containing the event
          loadAvailabilitiesForDateRange(
            new Date(eventData.start),
            view as 'month' | 'week' | 'day' | 'agenda'
          );
          setEvent(null);
          setSelectedEvent(null);
        }

        // Return the original result
        return result;
      } catch (error) {
        console.error('Error creating/updating event:', error);
        throw error;
      }
    },
    [saveItem, updateItem, loadAvailabilitiesForDateRange, fetchAvailabilities, operator]
  );

  const handleSelectEvent = useCallback(
    (eventToSelect: Event) => {
      setEvent(eventToSelect);
      setSelectedEvent({ ...eventToSelect, isModified: true } as ModifiedEvent);
    },
    [setEvent, setSelectedEvent]
  );

  const handleSelectSlot = useCallback(
    ({ start, end }: { start: Date; end: Date }) => {
      // Convert the calendar's native dates to Europe/Paris timezone for consistent handling
      const parisStart = dayjs(start).tz('Europe/Paris');
      const parisEnd = dayjs(end).tz('Europe/Paris');

      // Calculate the actual duration of the selected slot
      const selectedDuration = parisEnd.diff(parisStart, 'minute');

      // Arrondir au début de l'heure (minutes et secondes à 0)
      const roundedStart = parisStart.startOf('hour');

      // Set end time to preserve the actual selected duration
      const roundedEnd = roundedStart.add(selectedDuration, 'minute');

      const eventToBeCreated = {
        start: roundedStart.toDate(),
        end: roundedEnd.toDate(),
        allDay: false,
        title: 'Nouvelle disponibilité',
        eventType: 'availability',
        isModified: true,
        meeting: undefined,
      } as ModifiedEvent;
      setEvent(eventToBeCreated);
      setSelectedEvent(eventToBeCreated);
    },
    [setEvent, setSelectedEvent]
  );

  const handleEventDrop = useCallback(
    ({
      event: droppedEvent,
      start,
      end,
      isAllDay: droppedOnAllDaySlot = false,
    }: {
      event: Event;
      start: Date;
      end: Date;
      isAllDay: boolean;
    }) => {
      const { allDay } = droppedEvent;
      const updatedEvent = { ...droppedEvent };
      if (!allDay && droppedOnAllDaySlot) {
        updatedEvent.allDay = true;
      }
      if (allDay && !droppedOnAllDaySlot) {
        updatedEvent.allDay = false;
      }

      // Convert to Europe/Paris timezone for consistent handling
      const correctedStart = dayjs(start).tz('Europe/Paris').toDate();
      const correctedEnd = dayjs(end).tz('Europe/Paris').toDate();

      handleSubmitEvent({
        ...updatedEvent,
        start: correctedStart,
        end: correctedEnd,
        allDay: updatedEvent.allDay,
        recurrenceEditMode: 'single',
      } as any);
    },
    [handleSubmitEvent]
  );

  const handleEventResize = useCallback(
    ({ event: resizedEvent, start, end }: { event: Event; start: Date; end: Date }) => {
      // Convert to Europe/Paris timezone for consistent handling
      const correctedStart = dayjs(start).tz('Europe/Paris').toDate();
      const correctedEnd = dayjs(end).tz('Europe/Paris').toDate();

      handleSubmitEvent({
        ...resizedEvent,
        start: correctedStart,
        end: correctedEnd,
        recurrenceEditMode: 'single',
      } as any);
    },
    [handleSubmitEvent]
  );

  if (isAvailabilitiesFetching && !availabilities) {
    return <PageLoading />;
  }

  return (
    <div className="mx-auto max-w-full py-6 px-6 text-slate-500 text-base">
      <StickyWrapper isHeader>
        {/** Header */}
        <div className="flex justify-center relative lg:min-h-[70px]">
          <div className="flex flex-col lg:flex-row justify-between lg:items-center bg-white px-4 pt-4 pb-4 border border-slate-150 w-full rounded-3xl">
            <div className="flex flex-col flex-1">
              <h1 className="flex items-center text-2xl font-bold gap-2 text-primary">
                <BiCalendarCheck className="w-6 h-6" />
                {t('titlePages.availability')}
              </h1>
              <div className="text-fourth">{t('titleLegends.availability')}</div>
            </div>

            <Link
              to={`/mediators/edit/${mediatorUser?.id}`}
              className="flex w-auto mr-2 ml-auto lg:ml-0"
            >
              <Button
                title={t('actions.editMyProfile')}
                type="warning-line"
                icon="PencilSquareIcon"
              />
            </Link>
          </div>
        </div>
      </StickyWrapper>

      <div className="responsive-flex gap-2 mt-2">
        <div className="flex-1">
          <div className="flex flex-col gap-2 w-full">
            {/* Contenu principal */}
            <div className="responsive-flex flex flex-col 2xl:flex-row gap-2 items-start">
              {/* Colonne 1 - Calendrier (2/3) */}
              <div className="white-box responsive-flex gap-2 flex-col w-full 2xl:w-2/3">
                <div className="responsive-flex py-2 px-4 flex-col rounded">
                  <Calendar
                    events={
                      !availabilities
                        ? []
                        : selectedEvent
                        ? [
                            ...availabilities.filter((e) => e.id !== selectedEvent.id),
                            selectedEvent,
                          ]
                        : availabilities
                    }
                    style={{ minHeight: '600px' }}
                    defaultView="week"
                    activeViews={{ month: true, week: true, day: false, agenda: false }}
                    selectable
                    onSelectEvent={handleSelectEvent}
                    onSelectSlot={handleSelectSlot}
                    onRangeChange={handleRangeChange}
                    onEventDrop={handleEventDrop}
                    onEventResize={handleEventResize}
                  />
                </div>
              </div>

              {/* Colonne 2 - Informations du médiateur (1/3) */}
              <div className="gap-2 flex-col w-full 2xl:w-1/3">
                {/* Établissements */}
                <div className="white-box flex flex-col flex-1 gap-2 mt-2 xl:mt-0">
                  <SectionTitle
                    title={t('forms.establishments')}
                    icon="EstablishmentIcon"
                    className="text-fourth mt-2"
                  />
                  <div className="responsive-flex py-2 px-4 flex-col rounded">
                    {operator?.establishments && operator?.establishments.length > 0 ? (
                      <div className="flex flex-col gap-2">
                        {operator?.establishments.map((establishment) => (
                          <div
                            key={establishment.id}
                            className="flex flex-row items-center border gap-2 px-2 py-1 rounded-sm"
                          >
                            <Icon name="EstablishmentIcon" className="w-4 h-4" />
                            <span>
                              {`${establishment?.name} (${String(
                                establishment?.department?.CP
                              ).padStart(2, '0')})`}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p>{t('forms.noEstablishments')}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <EventDrawer
        event={event}
        setEvent={setEvent}
        setSelectedEvent={setSelectedEvent}
        onSubmit={handleSubmitEvent}
        onEventChanged={handleEventChanged}
        onDeleteEvent={handleDeleteEvent}
        newEventType="availability"
      />
    </div>
  );
}

export default Availability;
